import { motion } from 'motion/react';
import { ArrowLeft, Heart, Share2, Eye, Box } from 'lucide-react';
import type { Model3D } from '@visionar/shared';
import { useModelsStore } from '@/stores/models-store';
import { useUIStore } from '@/stores/ui-store';
import { useAuthStore } from '@/stores/auth-store';
import Button from '@/components/ui/Button';

interface ModelDetailProps {
  model: Model3D;
}

export default function ModelDetail({ model }: ModelDetailProps) {
  const { isFavorite, toggleFavorite } = useModelsStore();
  const { setSelectedModel, setARActive, setARModel } = useUIStore();
  const { isAuthenticated } = useAuthStore();
  const isFav = isFavorite(model.id);

  const handleClose = () => {
    setSelectedModel(null);
  };

  const handleFavorite = async () => {
    if (!isAuthenticated) return;
    try {
      await toggleFavorite(model.id);
    } catch {
      // Error handled by store
    }
  };

  const handleViewInAR = () => {
    setARModel(model);
    setARActive(true);
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(price);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: '100%' }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: '100%' }}
      transition={{ type: 'spring', damping: 25, stiffness: 300 }}
      className="fixed inset-0 bg-background z-[100] overflow-hidden"
    >
      {/* Hero Image */}
      <div className="relative h-[45%]">
        <img
          src={model.thumbnailUrl}
          alt={model.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />

        {/* Top Bar */}
        <div className="absolute top-0 left-0 right-0 p-4 pt-12 flex justify-between items-center safe-area-top">
          <button
            onClick={handleClose}
            className="w-12 h-12 rounded-full glass-dark flex items-center justify-center touch-feedback"
          >
            <ArrowLeft size={24} />
          </button>
          <div className="flex gap-3">
            {isAuthenticated && (
              <button
                onClick={handleFavorite}
                className="w-12 h-12 rounded-full glass-dark flex items-center justify-center touch-feedback"
              >
                <Heart
                  size={22}
                  className={isFav ? 'text-red-500 fill-red-500' : 'text-white'}
                />
              </button>
            )}
            <button className="w-12 h-12 rounded-full glass-dark flex items-center justify-center touch-feedback">
              <Share2 size={22} />
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="relative -mt-8 px-6 pb-32">
        <div className="glass rounded-t-[32px] p-6">
          {/* Brand & Name */}
          <p className="text-accent-blue text-xs font-bold tracking-wider uppercase mb-2">
            {model.brand?.name}
          </p>
          <h1 className="text-3xl font-bold mb-2">{model.name}</h1>

          {/* Price & Stats */}
          <div className="flex items-center justify-between mb-6">
            <span className="text-2xl font-bold gradient-text">
              {formatPrice(model.price)}
            </span>
            <div className="flex items-center gap-4 text-white/50 text-sm">
              <span className="flex items-center gap-1.5">
                <Eye size={16} />
                {model.viewCount.toLocaleString()}
              </span>
              <span className="flex items-center gap-1.5">
                <Box size={16} />
                3D
              </span>
            </div>
          </div>

          {/* Description */}
          <p className="text-white/70 leading-relaxed mb-6">{model.description}</p>

          {/* Colors */}
          {model.colors && model.colors.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold mb-3">Available Colors</h3>
              <div className="flex gap-3">
                {model.colors.map((color, i) => (
                  <button
                    key={color.id}
                    className={`w-10 h-10 rounded-full border-2 transition-transform hover:scale-110 ${
                      i === 0 ? 'border-white' : 'border-transparent'
                    }`}
                    style={{ backgroundColor: color.hexValue }}
                    title={color.name}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Category */}
          <div className="flex items-center gap-2 mb-6">
            <span className="text-white/50 text-sm">Category:</span>
            <span className="px-3 py-1 rounded-full glass text-sm">
              {model.category?.name}
            </span>
          </div>
        </div>
      </div>

      {/* Bottom CTA */}
      <div className="absolute bottom-0 left-0 right-0 p-6 pb-8 safe-area-bottom bg-gradient-to-t from-background via-background to-transparent">
        <Button fullWidth size="lg" onClick={handleViewInAR}>
          View in AR
        </Button>
      </div>
    </motion.div>
  );
}
